import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Stack,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useLogs, useLogLevels } from "../hooks/useLogs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const LEVEL_CHIP_COLORS: Record<string, { bg: string; color: string }> = {
    error:       { bg: 'rgba(239,68,68,0.1)',   color: '#DC2626' },
    fatal:       { bg: 'rgba(239,68,68,0.15)',  color: '#B91C1C' },
    warning:     { bg: 'rgba(245,158,11,0.1)',  color: '#D97706' },
    information: { bg: 'rgba(37,99,235,0.08)',  color: '#2563EB' },
    debug:       { bg: 'rgba(100,116,139,0.08)', color: '#64748B' },
    verbose:     { bg: 'rgba(100,116,139,0.06)', color: '#94A3B8' },
};

const chipSx = { height: 22, borderRadius: 1, fontWeight: 600, fontSize: '0.7rem', border: 'none' };

export default function LogsPage() {
    const { t, i18n } = useTranslation();
    const [selectedLevel, setSelectedLevel] = useState<string>("");
    const [count, setCount] = useState<number>(100);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const { data: levels } = useLogLevels();
    const { data: logs, isLoading, error } = useLogs({
        count,
        level: selectedLevel || undefined,
    });

    const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

    return (
        <Box sx={{ height: '100%', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column', p: 2, gap: 1.5, boxSizing: 'border-box', overflow: 'hidden' }}>
            {/* Filtros */}
            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none', flexShrink: 0 }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>{t('logs.logLevel')}</InputLabel>
                            <Select
                                value={selectedLevel}
                                onChange={(e) => { setSelectedLevel(e.target.value); setPage(0); }}
                                label={t('logs.logLevel')}
                            >
                                <MenuItem value="">{t('logs.all')}</MenuItem>
                                {levels?.map((level) => (
                                    <MenuItem key={level} value={level}>{level}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('logs.recordCount')}
                            type="number"
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                            slotProps={{ htmlInput: { min: 10, max: 1000, step: 10 } }}
                            sx={{ minWidth: 200 }}
                        />
                    </Stack>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ flexShrink: 0 }}>
                    {t('logs.loadError')}
                </Alert>
            )}

            {isLoading ? (
                <Card sx={{ flex: 1, border: '1px solid #E2E8F0', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: '#64748B' }}>{t('logs.loading')}</Typography>
                    </Box>
                </Card>
            ) : logs && logs.length > 0 ? (
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden', minHeight: 0 }}>
                    <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {[t('logs.colDateTime'), t('logs.colLevel'), t('logs.colMessage')].map((label, i) => (
                                        <TableCell key={label} sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', width: i === 0 ? 180 : i === 1 ? 120 : undefined }}>
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{ '&:hover': { bgcolor: '#F8FAFC' }, bgcolor: log.exception ? 'rgba(239,68,68,0.04)' : 'inherit' }}
                                    >
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#64748B' }}>
                                            {log.timestamp ? new Date(log.timestamp).toLocaleString(locale) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const style = LEVEL_CHIP_COLORS[(log.level ?? '').toLowerCase()] ?? { bg: 'rgba(100,116,139,0.08)', color: '#64748B' };
                                                return <Chip size="small" label={log.level} sx={{ ...chipSx, bgcolor: style.bg, color: style.color }} />;
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#1E293B' }}>{log.message}</Typography>
                                            {log.exception && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ display: 'block', mt: 1, fontFamily: 'monospace', bgcolor: 'rgba(239,68,68,0.06)', color: '#DC2626', p: 1, borderRadius: 1 }}
                                                >
                                                    {log.exception}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={logs.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage={t('common.rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => t('common.displayedRows', { from, to, count })}
                        sx={{ flexShrink: 0, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}
                    />
                </Card>
            ) : (
                <Card sx={{ flex: 1, border: '1px solid #E2E8F0', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#64748B' }}>{t('logs.empty')}</Typography>
                        <Typography variant="body2" sx={{ color: '#94A3B8', mt: 1 }}>
                            {t('logs.emptyDesc')}
                        </Typography>
                    </Box>
                </Card>
            )}
        </Box>
    );
}
